import { useState, useEffect, useCallback } from 'react';
import api from '../api/axios';

/**
 * Custom hook for managing tours list and operations
 * Handles fetching, filtering, status changes, and deletion
 */
export const useTours = (filters, setToast, t) => {
    const [tours, setTours] = useState([]);
    const [isStatusChangeModalOpen, setIsStatusChangeModalOpen] = useState(false);
    const [statusChangeData, setStatusChangeData] = useState({ tourId: null, newStatus: '', message: '', successMessage: '' });
    const [isDeleteTourModalOpen, setIsDeleteTourModalOpen] = useState(false);
    const [tourToDelete, setTourToDelete] = useState(null);

    const fetchTours = useCallback(async () => {
        try {
            // Remove empty filters
            const params = Object.fromEntries(
                Object.entries(filters).filter(([_, v]) => v !== '')
            );
            const res = await api.get('/client/tours', { params });
            setTours(Array.isArray(res.data) ? res.data : []);
        } catch (err) {
            console.error('Failed to fetch tours:', err);
            setTours([]);
        }
    }, [filters]);

    // Initial fetch and filter changes
    useEffect(() => {
        fetchTours();
    }, [fetchTours]);

    // Auto-refresh tours every X seconds to update view counts
    useEffect(() => {
        const refreshInterval = parseInt(import.meta.env.VITE_AUTO_REFRESH_INTERVAL) || 60000;
        const interval = setInterval(() => {
            fetchTours();
        }, refreshInterval);

        return () => clearInterval(interval);
    }, [fetchTours]);

    const handleStatusChange = (tourId, newStatus) => {
        let message = '';
        let successMessage = '';

        switch (newStatus) {
            case 'active':
                message = t('confirmPublish');
                successMessage = t('publishSuccess');
                break;
            case 'draft':
                message = t('confirmUnpublish');
                successMessage = t('unpublishSuccess');
                break;
            case 'reserved':
                message = t('confirmReserve');
                successMessage = t('reserveSuccess');
                break;
            case 'sold':
                message = t('confirmMarkSold');
                successMessage = t('soldSuccess');
                break;
            default:
                break;
        }

        setStatusChangeData({ tourId, newStatus, message, successMessage });
        setIsStatusChangeModalOpen(true);
    };

    const confirmStatusChange = async () => {
        try {
            const { tourId, newStatus, successMessage } = statusChangeData;
            await api.put(`/client/tours/${tourId}/status`, { status: newStatus });

            setTours(prevTours => prevTours.map(tour =>
                tour.id === tourId ? { ...tour, status: newStatus } : tour
            ));

            setToast({
                isVisible: true,
                message: successMessage,
                type: 'success'
            });
            setIsStatusChangeModalOpen(false);
        } catch (err) {
            console.error('Failed to update status:', err);
            setToast({
                isVisible: true,
                message: t('statusUpdateFailed'),
                type: 'error'
            });
            setIsStatusChangeModalOpen(false);
        }
    };

    const handleDeleteTour = (tourId) => {
        setTourToDelete(tourId);
        setIsDeleteTourModalOpen(true);
    };

    const confirmDeleteTour = async () => {
        if (!tourToDelete) return;
        try {
            await api.delete(`/tours/${tourToDelete}`);
            setTours(prevTours => prevTours.filter(t => t.id !== tourToDelete));
            setToast({
                isVisible: true,
                message: t('tourDeletedSuccessfully'),
                type: 'success'
            });
            setIsDeleteTourModalOpen(false);
            setTourToDelete(null);
        } catch (err) {
            console.error('Failed to delete tour:', err);
            setToast({
                isVisible: true,
                message: t('tourDeleteFailed'),
                type: 'error'
            });
            setIsDeleteTourModalOpen(false);
        }
    };

    return {
        tours,
        setTours,
        fetchTours,
        isStatusChangeModalOpen,
        setIsStatusChangeModalOpen,
        statusChangeData,
        setStatusChangeData,
        isDeleteTourModalOpen,
        setIsDeleteTourModalOpen,
        tourToDelete,
        setTourToDelete,
        handleStatusChange,
        confirmStatusChange,
        handleDeleteTour,
        confirmDeleteTour
    };
};
